import React, { useState, useEffect, useCallback } from 'react';
import { medicationHistoryService, MedicationHistoryImpact, MedicationHistoryConclusion } from '../lib/medicationHistoryService';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { Skeleton, SkeletonList } from './common/Skeleton';

interface MedicationHistoryViewProps {
  userId: string;
}

const rem = (px: number) => `${px / 16}rem`;

const fontSize = {
  xs: rem(12),
  sm: rem(14),
  base: rem(16),
  lg: rem(18),
  xl: rem(20),
  '2xl': rem(24)
};

export const MedicationHistoryView: React.FC<MedicationHistoryViewProps> = ({ userId }) => {
  const [impacts, setImpacts] = useState<MedicationHistoryImpact[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedicationId, setSelectedMedicationId] = useState<string | null>(null);
  const [conclusionFilter, setConclusionFilter] = useState<MedicationHistoryConclusion | 'all'>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [combinationFilter, setCombinationFilter] = useState<'all' | 'with_combinations' | 'solo_only'>('all');

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      // Timeout zabezpieczający przed zawieszeniem (30 sekund)
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: Analiza trwa zbyt długo')), 30000)
      );
      const dataPromise = medicationHistoryService.analyzeMedicationHistory(userId);
      const data = await Promise.race([dataPromise, timeoutPromise]) as MedicationHistoryImpact[];
      setImpacts(data);
    } catch (error) {
      console.error('Błąd ładowania historii leków:', error);
      // Ustaw puste dane zamiast pozostawić w stanie loading
      setImpacts([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const getConclusionLabel = (conclusion: MedicationHistoryConclusion): string => {
    switch (conclusion) {
      case 'improvement':
        return 'Obserwowano poprawę objawów';
      case 'no_change':
        return 'Brak wyraźnej zmiany';
      case 'worsening':
        return 'Objawy nasiliły się';
      case 'insufficient_data':
        return 'Brak wystarczających danych do oceny';
    }
  };

  const getConclusionColor = (conclusion: MedicationHistoryConclusion): string => {
    switch (conclusion) {
      case 'improvement':
        return '#00A86B'; // Zielony
      case 'no_change':
        return '#999999'; // Szary
      case 'worsening':
        return '#FF4444'; // Czerwony
      case 'insufficient_data':
        return '#FFA500'; // Pomarańczowy
    }
  };

  const filteredImpacts = (conclusionFilter === 'all' 
    ? impacts 
    : impacts.filter(impact => impact.conclusion === conclusionFilter))
    .filter(impact => {
      if (combinationFilter === 'all') return true;
      if (combinationFilter === 'with_combinations') {
        return impact.combinationAnalysis?.hasCombinations === true;
      }
      if (combinationFilter === 'solo_only') {
        return impact.combinationAnalysis?.hasCombinations === false;
      }
      return true;
    });

  const selectedImpact = selectedMedicationId 
    ? impacts.find(i => i.medicationId === selectedMedicationId)
    : null;

  if (loading) {
    return (
      <div style={{ padding: rem(24) }}>
        <Skeleton width="60%" height="24px" borderRadius="8px" />
        <div style={{ height: rem(16) }} />
        <SkeletonList count={4} />
      </div>
    );
  }

  if (impacts.length === 0) {
    return (
      <div style={{ padding: rem(24), textAlign: 'center' }}>
        <p style={{ color: '#999999', fontSize: fontSize.base }}>
          Brak leków do analizy. Dodaj leki, aby zobaczyć historię ich wpływu.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: rem(24) }}>
      {/* Disclaimer */}
      <div style={{
        padding: rem(16),
        background: '#F5F3FF',
        border: '1px solid #E0E7FF',
        borderRadius: rem(12),
        marginBottom: rem(24)
      }}>
        <p style={{
          fontSize: fontSize.sm,
          color: '#666666',
          margin: 0,
          lineHeight: 1.5
        }}>
          <strong>Uwaga:</strong> To podsumowanie obserwacji na podstawie Twoich wpisów. Nie stanowi diagnozy medycznej.
        </p>
      </div>

      {/* Filtry */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: rem(12),
        marginBottom: rem(24)
      }}>
        <div style={{
          display: 'flex',
          gap: rem(8),
          flexWrap: 'wrap',
          padding: rem(4),
          background: '#F5F5F5',
          borderRadius: rem(12),
          border: '1px solid #E5E5E5'
        }}>
        <button
          onClick={() => setConclusionFilter('all')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'all' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'all' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'all' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'all' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Wszystkie ({impacts.length})
        </button>
        <button
          onClick={() => setConclusionFilter('improvement')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'improvement' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'improvement' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'improvement' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'improvement' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Poprawa ({impacts.filter(i => i.conclusion === 'improvement').length})
        </button>
        <button
          onClick={() => setConclusionFilter('no_change')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'no_change' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'no_change' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'no_change' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'no_change' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Bez zmian ({impacts.filter(i => i.conclusion === 'no_change').length})
        </button>
        <button
          onClick={() => setConclusionFilter('worsening')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'worsening' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'worsening' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'worsening' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'worsening' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Pogorszenie ({impacts.filter(i => i.conclusion === 'worsening').length})
        </button>
        <button
          onClick={() => setConclusionFilter('insufficient_data')}
          style={{
            padding: `${rem(8)} ${rem(16)}`,
            borderRadius: rem(8),
            border: 'none',
            background: conclusionFilter === 'insufficient_data' ? '#FFFFFF' : 'transparent',
            color: conclusionFilter === 'insufficient_data' ? '#1A1A1A' : '#999999',
            fontSize: fontSize.sm,
            fontWeight: conclusionFilter === 'insufficient_data' ? 600 : 400,
            cursor: 'pointer',
            boxShadow: conclusionFilter === 'insufficient_data' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
          }}
        >
          Brak danych ({impacts.filter(i => i.conclusion === 'insufficient_data').length})
        </button>
        </div>

        {/* Filtry zaawansowane */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: rem(8)
        }}>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            style={{
              padding: `${rem(6)} ${rem(12)}`,
              background: 'transparent',
              border: '1px solid #E5E5E5',
              borderRadius: rem(8),
              fontSize: fontSize.xs,
              color: '#666666',
              cursor: 'pointer'
            }}
          >
            {showAdvancedFilters ? '▼' : '▶'} Filtry zaawansowane
          </button>
        </div>

        {showAdvancedFilters && (
          <div style={{
            display: 'flex',
            gap: rem(8),
            flexWrap: 'wrap',
            padding: rem(4),
            background: '#F9F9F9',
            borderRadius: rem(12),
            border: '1px solid #E5E5E5'
          }}>
            <button
              onClick={() => setCombinationFilter('all')}
              style={{
                padding: `${rem(6)} ${rem(12)}`,
                borderRadius: rem(8),
                border: 'none',
                background: combinationFilter === 'all' ? '#FFFFFF' : 'transparent',
                color: combinationFilter === 'all' ? '#1A1A1A' : '#999999',
                fontSize: fontSize.xs,
                fontWeight: combinationFilter === 'all' ? 600 : 400,
                cursor: 'pointer',
                boxShadow: combinationFilter === 'all' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Wszystkie
            </button>
            <button
              onClick={() => setCombinationFilter('with_combinations')}
              style={{
                padding: `${rem(6)} ${rem(12)}`,
                borderRadius: rem(8),
                border: 'none',
                background: combinationFilter === 'with_combinations' ? '#FFFFFF' : 'transparent',
                color: combinationFilter === 'with_combinations' ? '#1A1A1A' : '#999999',
                fontSize: fontSize.xs,
                fontWeight: combinationFilter === 'with_combinations' ? 600 : 400,
                cursor: 'pointer',
                boxShadow: combinationFilter === 'with_combinations' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Z kombinacjami ({impacts.filter(i => i.combinationAnalysis?.hasCombinations === true).length})
            </button>
            <button
              onClick={() => setCombinationFilter('solo_only')}
              style={{
                padding: `${rem(6)} ${rem(12)}`,
                borderRadius: rem(8),
                border: 'none',
                background: combinationFilter === 'solo_only' ? '#FFFFFF' : 'transparent',
                color: combinationFilter === 'solo_only' ? '#1A1A1A' : '#999999',
                fontSize: fontSize.xs,
                fontWeight: combinationFilter === 'solo_only' ? 600 : 400,
                cursor: 'pointer',
                boxShadow: combinationFilter === 'solo_only' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
              }}
            >
              Tylko samodzielne ({impacts.filter(i => i.combinationAnalysis?.hasCombinations === false).length})
            </button>
          </div>
        )}
      </div>

      {/* Widok szczegółowy */}
      {selectedImpact && (
        <div style={{
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(12),
          padding: rem(24),
          marginBottom: rem(24)
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: rem(20)
          }}>
            <div>
              <h2 style={{
                fontSize: fontSize.lg,
                fontWeight: 600,
                color: '#1A1A1A',
                margin: 0,
                marginBottom: rem(8)
              }}>
                {selectedImpact.medicationName}
              </h2>
              <div style={{
                fontSize: fontSize.sm,
                color: '#666666',
                marginBottom: rem(4)
              }}>
                Okres stosowania: {format(parseISO(selectedImpact.startDate), 'dd.MM.yyyy', { locale: pl })}
                {selectedImpact.endDate && ` — ${format(parseISO(selectedImpact.endDate), 'dd.MM.yyyy', { locale: pl })}`}
                {!selectedImpact.endDate && ' — obecnie'}
              </div>
              <div style={{
                fontSize: fontSize.sm,
                color: '#666666'
              }}>
                Status: {selectedImpact.status === 'active' ? 'Aktywny' : 'Zakończony'}
              </div>
            </div>
            <button
              onClick={() => setSelectedMedicationId(null)}
              style={{
                padding: rem(8),
                background: 'transparent',
                border: '1px solid #E5E5E5',
                borderRadius: rem(8),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                color: '#666666'
              }}
            >
              ✕
            </button>
          </div>

          {selectedImpact.hasEnoughData ? (
            <>
              {/* Porównanie przed/po */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: rem(16),
                marginBottom: rem(24)
              }}>
                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Dni zaostrzeń
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.daysWithFlare}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.daysWithFlare}</strong>
                  </div>
                  {selectedImpact.change.flareDaysChange !== 0 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.flareDaysChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.flareDaysChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.flareDaysChange)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średnia liczba wypróżnień
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgBmPerDay.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgBmPerDay.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgBmChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgBmChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgBmChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.avgBmChange).toFixed(1)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Dni z krwią
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.bloodDaysCount}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.bloodDaysCount}</strong>
                  </div>
                  {selectedImpact.change.bloodDaysChange !== 0 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.bloodDaysChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.bloodDaysChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.bloodDaysChange)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średni ból
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgPain.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgPain.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgPainChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgPainChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgPainChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.avgPainChange).toFixed(1)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średnia pilność
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgUrgency.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgUrgency.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgUrgencyChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgUrgencyChange < 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgUrgencyChange < 0 ? '↓' : '↑'} {Math.abs(selectedImpact.change.avgUrgencyChange).toFixed(1)}
                    </div>
                  )}
                </div>

                <div style={{
                  padding: rem(16),
                  background: '#F9F9F9',
                  borderRadius: rem(8),
                  border: '1px solid #E5E5E5'
                }}>
                  <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                    Średni poziom energii
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Przed: <strong>{selectedImpact.beforePeriod.avgEnergy.toFixed(1)}</strong>
                  </div>
                  <div style={{ fontSize: fontSize.sm, color: '#666666' }}>
                    Po: <strong>{selectedImpact.afterPeriod.avgEnergy.toFixed(1)}</strong>
                  </div>
                  {Math.abs(selectedImpact.change.avgEnergyChange) > 0.5 && (
                    <div style={{
                      fontSize: fontSize.xs,
                      color: selectedImpact.change.avgEnergyChange > 0 ? '#00A86B' : '#FF4444',
                      marginTop: rem(4)
                    }}>
                      {selectedImpact.change.avgEnergyChange > 0 ? '↑' : '↓'} {Math.abs(selectedImpact.change.avgEnergyChange).toFixed(1)}
                    </div>
                  )}
                </div>
              </div>

              {/* Podsumowanie */}
              <div style={{
                padding: rem(16),
                background: '#F5F3FF',
                borderRadius: rem(8),
                border: `2px solid ${getConclusionColor(selectedImpact.conclusion)}`,
                marginBottom: rem(16)
              }}>
                <div style={{
                  fontSize: fontSize.base,
                  fontWeight: 600,
                  color: getConclusionColor(selectedImpact.conclusion),
                  marginBottom: rem(8)
                }}>
                  {getConclusionLabel(selectedImpact.conclusion)}
                </div>
                <div style={{
                  fontSize: fontSize.sm,
                  color: '#666666',
                  lineHeight: 1.5
                }}>
                  {selectedImpact.summary}
                </div>
              </div>

              {/* Informacja o okresie analizy */}
              <div style={{
                fontSize: fontSize.xs,
                color: '#999999',
                fontStyle: 'italic',
                marginBottom: rem(24)
              }}>
                Analiza obejmuje okres przed rozpoczęciem leku ({format(parseISO(selectedImpact.beforePeriod.periodStart), 'dd.MM.yyyy', { locale: pl })} — {format(parseISO(selectedImpact.beforePeriod.periodEnd), 'dd.MM.yyyy', { locale: pl })})
                {' '}oraz okres po rozpoczęciu ({format(parseISO(selectedImpact.afterPeriod.periodStart), 'dd.MM.yyyy', { locale: pl })} — {format(parseISO(selectedImpact.afterPeriod.periodEnd), 'dd.MM.yyyy', { locale: pl })}).
              </div>

              {/* Sekcja kombinacji leków */}
              {selectedImpact.combinationAnalysis && (
                <div style={{
                  marginTop: rem(32),
                  paddingTop: rem(24),
                  borderTop: '1px solid #E5E5E5'
                }}>
                  <h3 style={{
                    fontSize: fontSize.base,
                    fontWeight: 600,
                    color: '#1A1A1A',
                    margin: 0,
                    marginBottom: rem(12)
                  }}>
                    Stosowanie w połączeniu z innymi lekami
                  </h3>
                  
                  {/* Disclaimer */}
                  <div style={{
                    padding: rem(12),
                    background: '#FFF5E6',
                    border: '1px solid #FFE0B2',
                    borderRadius: rem(8),
                    marginBottom: rem(16)
                  }}>
                    <p style={{
                      fontSize: fontSize.xs,
                      color: '#666666',
                      margin: 0,
                      lineHeight: 1.5
                    }}>
                      Analiza pokazuje obserwacje z danych historycznych. Nie pozwala określić przyczyn ani skuteczności klinicznej połączeń leków.
                    </p>
                  </div>

                  {selectedImpact.combinationAnalysis.hasCombinations ? (
                    <>
                      {/* Ogólne podsumowanie */}
                      <div style={{
                        padding: rem(12),
                        background: '#F9F9F9',
                        borderRadius: rem(8),
                        marginBottom: rem(16)
                      }}>
                        <p style={{
                          fontSize: fontSize.sm,
                          color: '#666666',
                          margin: 0,
                          lineHeight: 1.5
                        }}>
                          {selectedImpact.combinationAnalysis.summary}
                        </p>
                      </div>

                      {/* Lista kombinacji */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: rem(16) }}>
                        {selectedImpact.combinationAnalysis.combinations.map((combination, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: rem(16),
                              background: '#F9F9F9',
                              borderRadius: rem(8),
                              border: '1px solid #E5E5E5'
                            }}
                          >
                            <div style={{
                              fontSize: fontSize.base,
                              fontWeight: 600,
                              color: '#1A1A1A',
                              marginBottom: rem(12)
                            }}>
                              {combination.combinationName}
                            </div>

                            {/* Porównanie samodzielny vs w połączeniu */}
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                              gap: rem(12),
                              marginBottom: rem(12)
                            }}>
                              <div>
                                <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                                  Samodzielnie ({combination.soloPeriod.daysCount} dni)
                                </div>
                                <div style={{ fontSize: fontSize.xs, color: '#666666' }}>
                                  Zaostrzenia: <strong>{combination.soloPeriod.daysWithFlare}</strong>
                                </div>
                                <div style={{ fontSize: fontSize.xs, color: '#666666' }}>
                                  Wypróżnienia: <strong>{combination.soloPeriod.avgBmPerDay.toFixed(1)}</strong>
                                </div>
                                <div style={{ fontSize: fontSize.xs, color: '#666666' }}>
                                  Krew: <strong>{combination.soloPeriod.bloodDaysCount}</strong>
                                </div>
                              </div>

                              <div>
                                <div style={{ fontSize: fontSize.xs, color: '#999999', marginBottom: rem(4) }}>
                                  W połączeniu ({combination.combinationPeriod.daysCount} dni)
                                </div>
                                <div style={{ fontSize: fontSize.xs, color: '#666666' }}>
                                  Zaostrzenia: <strong>{combination.combinationPeriod.daysWithFlare}</strong>
                                  {combination.difference.flareDaysChange !== 0 && (
                                    <span style={{
                                      color: combination.difference.flareDaysChange < 0 ? '#00A86B' : '#FF4444',
                                      marginLeft: rem(4)
                                    }}>
                                      ({combination.difference.flareDaysChange < 0 ? '↓' : '↑'} {Math.abs(combination.difference.flareDaysChange)})
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: fontSize.xs, color: '#666666' }}>
                                  Wypróżnienia: <strong>{combination.combinationPeriod.avgBmPerDay.toFixed(1)}</strong>
                                  {Math.abs(combination.difference.avgBmChange) > 0.5 && (
                                    <span style={{
                                      color: combination.difference.avgBmChange < 0 ? '#00A86B' : '#FF4444',
                                      marginLeft: rem(4)
                                    }}>
                                      ({combination.difference.avgBmChange < 0 ? '↓' : '↑'} {Math.abs(combination.difference.avgBmChange).toFixed(1)})
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: fontSize.xs, color: '#666666' }}>
                                  Krew: <strong>{combination.combinationPeriod.bloodDaysCount}</strong>
                                  {combination.difference.bloodDaysChange !== 0 && (
                                    <span style={{
                                      color: combination.difference.bloodDaysChange < 0 ? '#00A86B' : '#FF4444',
                                      marginLeft: rem(4)
                                    }}>
                                      ({combination.difference.bloodDaysChange < 0 ? '↓' : '↑'} {Math.abs(combination.difference.bloodDaysChange)})
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Wniosek */}
                            <div style={{
                              padding: rem(12),
                              background: '#FFFFFF',
                              borderRadius: rem(8),
                              fontSize: fontSize.sm,
                              color: '#666666',
                              lineHeight: 1.5
                            }}>
                              {combination.summary}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div style={{
                      padding: rem(16),
                      background: '#F9F9F9',
                      borderRadius: rem(8),
                      fontSize: fontSize.sm,
                      color: '#666666'
                    }}>
                      {selectedImpact.combinationAnalysis.summary}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{
              padding: rem(16),
              background: '#FFF5E6',
              borderRadius: rem(8),
              border: '1px solid #FFE0B2'
            }}>
              <p style={{
                fontSize: fontSize.sm,
                color: '#666666',
                margin: 0
              }}>
                {selectedImpact.summary}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Lista leków */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: rem(16)
      }}>
        {filteredImpacts.map((impact) => (
          <div
            key={impact.medicationId}
            onClick={() => setSelectedMedicationId(impact.medicationId)}
            style={{
              padding: rem(20),
              background: '#FFFFFF',
              border: `2px solid ${getConclusionColor(impact.conclusion)}`,
              borderRadius: rem(12),
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <h3 style={{
              fontSize: fontSize.base,
              fontWeight: 600,
              color: '#1A1A1A',
              margin: 0,
              marginBottom: rem(8)
            }}>
              {impact.medicationName}
            </h3>
            
            <div style={{
              fontSize: fontSize.xs,
              color: '#666666',
              marginBottom: rem(8)
            }}>
              {format(parseISO(impact.startDate), 'dd.MM.yyyy', { locale: pl })}
              {impact.endDate && ` — ${format(parseISO(impact.endDate), 'dd.MM.yyyy', { locale: pl })}`}
              {!impact.endDate && ' — obecnie'}
            </div>
            
            <div style={{
              fontSize: fontSize.xs,
              color: '#999999',
              marginBottom: rem(12)
            }}>
              {impact.status === 'active' ? 'Aktywny' : 'Zakończony'}
            </div>
            
            <div style={{
              padding: rem(12),
              background: '#F5F3FF',
              borderRadius: rem(8),
              border: `1px solid ${getConclusionColor(impact.conclusion)}`
            }}>
              <div style={{
                fontSize: fontSize.sm,
                fontWeight: 600,
                color: getConclusionColor(impact.conclusion),
                marginBottom: rem(4)
              }}>
                {getConclusionLabel(impact.conclusion)}
              </div>
              {impact.hasEnoughData && (
                <div style={{
                  fontSize: fontSize.xs,
                  color: '#666666',
                  lineHeight: 1.4
                }}>
                  {impact.summary.substring(0, 100)}
                  {impact.summary.length > 100 && '...'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
